import { User } from '@sr/types';
import * as React from 'react';
import { useState, useEffect, useContext, createContext } from 'react';
import { ref } from '../utils/firebase';
import { useAuth } from './use-auth';
import { trackIsProMixpanel } from 'utils/analytics';
import isPro from 'v1/utils/is-pro';

const userContext = createContext<User | false | null>(null);

// Provider component that wraps your app and makes auth object
// available to any child component that calls useUser().
export function ProvideUser({ children }: { children: React.ReactNode }) {
  const user = useProvideUser();
  return <userContext.Provider value={user}>{children}</userContext.Provider>;
}

// Hook for child components to get the user object and re-render when it
// changes.
export const useUser = (): User | null | false => {
  return useContext(userContext);
};

// Provider hook that creates user object and handles state
function useProvideUser() {
  const auth = useAuth();

  // Null is used before any response is received
  // False means that an empty reponse was received
  const [user, setUser] = useState<User | false | null>(null);

  // Subscribe to user on mount
  // Because this sets state in the callback it will cause any component that
  // utilizes this hook to re-render with the latest user object.
  useEffect(() => {
    let unsubscribe = () => {};

    if (auth.user) {
      // Listen for changes to the user in the db and update user state
      const listener = ref
        .child(`users2/${auth.user.uid}`)
        .on('value', (snap) => {
          const user: User = snap.val();
          if (user) {
            if (isPro(user)) {
              trackIsProMixpanel(true);
            }
            setUser(user);
          } else {
            setUser(false);
          }
        });

      // Remove the listener for user changes
      unsubscribe = () => {
        ref.off('value', listener);
      };
    }

    return () => unsubscribe();
  }, [auth.user]);

  // Return the user
  return user;
}

// null is used to indicate loading
export function useIsProUser(): boolean | null {
  const user = useUser();
  if (!user) return null;
  if (isPro(user)) return true;
  return false;
}
